/**
 * BugReportPanel - Lightweight bug/feedback form panel
 * Submits tickets to 0x3admin API
 */

import { useState, useEffect } from 'react'
import {
  Bug,
  MessageSquare,
  X,
  Send,
  Loader2,
  CheckCircle,
  AlertCircle,
  Lightbulb,
  HelpCircle,
} from 'lucide-react'
import { cn } from '../../lib/utils'
import { ADMIN_API_URL } from './types'

const FEEDBACK_TYPES = [
  { id: 'bug', label: 'Bug', icon: Bug, color: 'rose' },
  { id: 'feedback', label: 'Feedback', icon: MessageSquare, color: 'blue' },
  { id: 'feature_request', label: 'Feature', icon: Lightbulb, color: 'amber' },
  { id: 'question', label: 'Question', icon: HelpCircle, color: 'purple' },
] as const

export function BugReportPanel({
  isOpen,
  onClose,
  user,
}: {
  isOpen: boolean
  onClose: () => void
  user?: { id?: string; email?: string; name?: string } | null
}) {
  const [step, setStep] = useState<'type' | 'details' | 'success'>('type')
  const [selectedType, setSelectedType] = useState<string | null>(null)
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ticketId, setTicketId] = useState<string | null>(null)

  useEffect(() => {
    if (isOpen) {
      setStep('type')
      setSelectedType(null)
      setTitle('')
      setDescription('')
      setError(null)
      setTicketId(null)
    }
  }, [isOpen])

  const handleSubmit = async () => {
    if (!title.trim()) {
      setError('Please enter a title')
      return
    }
    setIsSubmitting(true)
    setError(null)

    try {
      const token =
        localStorage.getItem('IdToken') ||
        localStorage.getItem('access_token')

      if (!token) {
        setError('Please log in to submit feedback.')
        setIsSubmitting(false)
        return
      }

      const payload = {
        user_id: user?.id || 'anonymous',
        user_email: user?.email || null,
        org_id: localStorage.getItem('org_id') || localStorage.getItem('CurrentTenantId') || null,
        type: selectedType,
        title: title.trim(),
        description: description.trim() || null,
        page_url: window.location.href,
        page_context: { route: window.location.pathname, timestamp: new Date().toISOString() },
        browser_info: navigator.userAgent,
        priority: selectedType === 'bug' ? 'high' : 'normal',
      }

      const res = await fetch(`${ADMIN_API_URL}/api/support/tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      })

      if (!res.ok) {
        if (res.status === 401) throw new Error('Session expired. Please refresh.')
        throw new Error('Failed to submit. Please try again.')
      }

      const data = await res.json()
      setTicketId(data.id)
      setStep('success')
    } catch (err: any) {
      setError(err.message || 'Failed to submit.')
    } finally {
      setIsSubmitting(false)
    }
  }

  if (!isOpen) return null

  return (
    <div className="w-80 bg-card rounded-t-xl shadow-2xl border border-border border-b-0 overflow-hidden animate-in slide-in-from-bottom-2 duration-200">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 bg-gradient-to-r from-rose-500 to-pink-600">
        <div className="flex items-center gap-2">
          <Bug className="w-4 h-4 text-white" />
          <span className="text-sm font-semibold text-white">
            {step === 'success' ? 'Submitted!' : 'Report Issue'}
          </span>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-white/20 rounded">
          <X className="w-4 h-4 text-white" />
        </button>
      </div>

      {/* Content */}
      <div className="p-3 max-h-[350px] overflow-y-auto">
        {error && (
          <div className="mb-3 p-2 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 text-xs flex items-center gap-2">
            <AlertCircle className="w-3 h-3" /> {error}
          </div>
        )}

        {step === 'type' && (
          <div className="space-y-3">
            <p className="text-xs text-slate-500">What would you like to report?</p>
            <div className="grid grid-cols-4 gap-1.5">
              {FEEDBACK_TYPES.map((type) => {
                const Icon = type.icon
                const colorMap: Record<string, string> = {
                  rose: 'border-rose-200 hover:border-rose-400 hover:bg-rose-50 dark:hover:bg-rose-950/20',
                  blue: 'border-blue-200 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/20',
                  amber: 'border-amber-200 hover:border-amber-400 hover:bg-amber-50 dark:hover:bg-amber-950/20',
                  purple: 'border-purple-200 hover:border-purple-400 hover:bg-purple-50 dark:hover:bg-purple-950/20',
                }
                const textColorMap: Record<string, string> = {
                  rose: 'text-rose-500',
                  blue: 'text-blue-500',
                  amber: 'text-amber-500',
                  purple: 'text-purple-500',
                }
                return (
                  <button
                    key={type.id}
                    onClick={() => { setSelectedType(type.id); setStep('details') }}
                    className={cn(
                      'p-2 rounded-lg border transition-all text-center hover:scale-105',
                      colorMap[type.color]
                    )}
                  >
                    <Icon className={cn('w-4 h-4 mx-auto mb-1', textColorMap[type.color])} />
                    <span className="text-[10px] text-muted-foreground">{type.label}</span>
                  </button>
                )
              })}
            </div>
          </div>
        )}

        {step === 'details' && (
          <div className="space-y-3">
            <button onClick={() => setStep('type')} className="text-xs text-muted-foreground hover:text-foreground">
              ← Back
            </button>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief title..."
              className="w-full px-3 py-2 rounded-lg border border-border text-sm focus:ring-2 focus:ring-rose-500 bg-card outline-none"
              autoFocus
            />
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Details (optional)..."
              rows={3}
              className="w-full px-3 py-2 rounded-lg border border-border text-sm resize-none focus:ring-2 focus:ring-rose-500 bg-card outline-none"
            />
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || !title.trim()}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-rose-500 hover:bg-rose-600 text-white text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <><Send className="w-3 h-3" /> Submit</>
              )}
            </button>
          </div>
        )}

        {step === 'success' && (
          <div className="text-center py-4">
            <CheckCircle className="w-10 h-10 mx-auto text-emerald-500 mb-2" />
            <p className="text-sm font-medium mb-1">Thank you!</p>
            <p className="text-xs text-slate-400">Ticket: {ticketId?.slice(0, 8)}</p>
            <button
              onClick={onClose}
              className="mt-3 px-4 py-1.5 rounded-lg border border-border text-sm hover:bg-muted transition-colors"
            >
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  )
}
